import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import {
  FileDetails,
  FileUploadStatus,
  PDMShareAction,
  SharingInfo,
  SharingStatus,
} from '../../models/file-details';
import { DocumentUploadComponent } from '../document-upload/document-upload.component';
import { DocumentShareComponent } from '../document-share/document-share.component';
import { DocumentOperationsModalComponent } from '../document-operations-modal/document-operations-modal.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { FileUploadService } from '../../services/file-upload.service';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { FileOperation } from '../../models/file-operation';
import { FormsModule } from '@angular/forms';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { AuthService } from '../../../auth/services/auth.service';
import { ManageConnectionService } from '../../../manage-connection-group/services/manage-connection.service';
import { Connection } from '../../../manage-connection-group/models/connection';
import { Auth0Connection } from '../../../../shared/models/auth0-connection';
import { ManageUsersService } from '../../../manage-users/services/manage-users.service';
import { User } from '../../../../shared/models/user';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import {
  ActiveFilter,
  FilterGroupComponent,
} from '../../../../shared/components/filter-group/filter-group.component';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { FileViewerComponent } from '../../../../shared/components/file-viewer/file-viewer.component';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  EntityType,
  ReportContentData,
} from '../../../../shared/interfaces/report-content';
import { ReportContentService } from '../../../../shared/services/reports/report-content.service';

@Component({
  selector: 'app-document-table',
  standalone: true,
  imports: [
    DocumentUploadComponent,
    DocumentShareComponent,
    DocumentOperationsModalComponent,
    PaginationComponent,
    SvgIconComponent,
    SecondaryPopupComponent,
    PageSizeComponent,
    FormsModule,
    PopupComponent,
    SearchInputComponent,
    FilterGroupComponent,
    DatePipe,
    NgClass,
    CommonModule,
    FileViewerComponent,
  ],
  templateUrl: './document-table.component.html',
  styleUrl: './document-table.component.scss',
})
export class DocumentTableComponent
  extends DestroyBase
  implements OnInit, OnDestroy
{
  fileUploadService = inject(FileUploadService);
  authService = inject(AuthService);
  manageConnectionService = inject(ManageConnectionService);
  manageUsersService = inject(ManageUsersService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  auditLogService = inject(AuditLogService);
  private commonService = inject(CommonService);
  private reportContentService = inject(ReportContentService);

  onReportFile(file: FileDetails): void {
    const payload: ReportContentData = {
      entityId: file.id,
      entityType: EntityType.FILE,
      title: 'Flag File',
      itemTitle: file.blob_name,
    };
    this.reportContentService.open(payload);
  }

  readonly isAdmin = this.authService.hasPermission(
    PermissionCode.ADMIN_PRIVILEGES,
  );

  showUploadPopup = false;
  loading = true;
  uploadedFiles!: FileDetails[];
  currentPage = 1;
  pageSize = 50;
  totalFiles = 0;
  fileOperations: FileOperation[] = [];
  connectionOperationsMap = new Map<string, FileOperation[]>();
  tourFileItem = 0;
  getModalFileOperations(file: FileDetails): FileOperation[] {
    if (!file) return [];
    if (this.isAdmin) {
      const connectionName = file.uploaded_user.connection.connection_name;
      return this.connectionOperationsMap.get(connectionName) || [];
    }
    return this.fileOperations;
  }

  connections: Connection[] = [];
  users: User[] = [];
  operationsFilter = 'all';
  operationOptions = [
    {
      option: 'All',
      value: 'all',
    },
  ];
  uploadedConnectionFilter = '';
  uploadedUserFilter = '';
  uploadStatusFilter = '';
  activeTab: 'my-files' | 'shared-files' | 'pdm-files' = 'my-files';
  pdmStatusFilter = '';

  // Applied filters (for badges)
  appliedOperationsFilter = 'all';
  appliedUploadedConnectionFilter = '';
  appliedUploadedUserFilter = '';
  appliedUploadStatusFilter = '';
  appliedPdmStatusFilter = '';
  showConfirmPopup = false;
  popupFile!: FileDetails;
  searchQuery = '';

  // Approval functionality properties
  showApprovePopup = false;
  approveFile!: FileDetails;
  approveSharingInfo: SharingInfo | null = null;
  loadingSharingInfo = false;
  SharingStatus = SharingStatus;

  // Share functionality properties
  showSharePopup = false;
  shareFile!: FileDetails;

  // Operations popup properties
  showOperationsPopup = false;
  operationsFile!: FileDetails;

  loggedInUsername = this.authService.getLoggedInUserInfo()?.username || '';

  // File Viewer properties
  showFileViewer = false;
  viewerFileUrl = '';
  viewerFileName = '';
  viewerFileId: number | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private refreshInterval: any;

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const shared = params['shared'] === 'true';
        const pdmOnly = params['pdm_only'] === 'true' && this.isAdmin;

        let newTab: 'my-files' | 'shared-files' | 'pdm-files' = 'my-files';
        if (pdmOnly) {
          newTab = 'pdm-files';
        } else if (shared) {
          newTab = 'shared-files';
        }

        if (this.activeTab !== newTab) {
          this.activeTab = newTab;
          this.resetFiltersState();

          // If navigating from dashboard with a specific status
          const pdmStatus = params['pdm_sharing_status'];
          if (pdmStatus && this.activeTab === 'pdm-files') {
            this.pdmStatusFilter = pdmStatus;
            this.appliedPdmStatusFilter = pdmStatus;
          }
        }
        this.loadFiles();
      });

    this.getFileOperations();
    if (this.isAdmin) {
      this.getConnections();
      this.getUsers();
    }
    this.startAutoRefresh();

    // Subscribe to upload cancellation events
    this.fileUploadService.uploadCancelled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadFiles(true);
      });
  }

  findFileItem(): void {
    if (!this.uploadedFiles?.length) return;

    for (const [index] of this.uploadedFiles.entries()) {
      const infoELe = document.getElementById(`tour-file-item-info-${index}`);
      const shareELe = document.getElementById(`tour-file-item-share-${index}`);
      const downloadELe = document.getElementById(
        `tour-file-item-download-${index}`,
      );

      if (infoELe && shareELe && downloadELe) {
        this.tourFileItem = index;

        infoELe.id = `tour-file-item-info`;
        shareELe.id = `tour-file-item-share`;
        downloadELe.id = `tour-file-item-download`;

        const deleteEle = document.getElementById(
          `tour-file-item-delete-${index}`,
        );

        if (deleteEle) {
          deleteEle.id = `tour-file-item-delete`;
        }

        break;
      }
    }
  }

  private startAutoRefresh(): void {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Refresh every 30 seconds to update file upload statuses
    this.refreshInterval = setInterval(() => {
      // Only refresh if there are uploading files
      if (
        this.uploadedFiles?.some(
          (file) => file.upload_status === FileUploadStatus.Pending,
        )
      ) {
        this.loadFiles(true);
      }
    }, 30000);
  }

  override ngOnDestroy(): void {
    // Clear the refresh interval when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    super.ngOnDestroy();
  }
  getFileExtension(filename: string): string {
    const ext = filename.split('.').pop()?.toUpperCase() || '';
    return ext.slice(0, 3);
  }

  getFileIconClass(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const classMap: Record<string, string> = {
      csv: 'csv',
      xlsx: 'xlsx',
      xls: 'xlsx',
      xlsm: 'xlsx',
      pdf: 'pdf',
      doc: 'doc',
      docx: 'doc',
    };
    return classMap[ext] || 'default';
  }

  loadFiles(skipLoader = false): void {
    const operationsParam =
      this.operationsFilter === 'all' ? undefined : this.operationsFilter;
    const searchParam = this.searchQuery || undefined;
    const connectionParam = this.uploadedConnectionFilter || undefined;
    // If "My Files" tab is active, use the logged-in username
    const userParam =
      this.activeTab === 'my-files'
        ? this.loggedInUsername
        : this.uploadedUserFilter || undefined;
    const statusParam = this.uploadStatusFilter || undefined;

    // For non-admin users, when on "Shared Files" tab, set shared_with_me=true
    const sharedWithMeParam =
      !this.isAdmin && this.activeTab === 'shared-files' ? true : undefined;

    const pdmOnlyParam = this.activeTab === 'pdm-files' ? true : undefined;

    this.fileUploadService
      .getAllUploadedFiles(
        this.currentPage,
        this.pageSize,
        skipLoader,
        operationsParam,
        searchParam,
        connectionParam,
        userParam,
        statusParam,
        sharedWithMeParam,
        pdmOnlyParam,
        this.pdmStatusFilter || undefined,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.uploadedFiles = res.results;
        this.totalFiles = res.count;
        this.loading = false;

        if (this.isAdmin) {
          const connectionNames = [
            ...new Set(
              this.uploadedFiles.map(
                (f) => f.uploaded_user.connection.connection_name,
              ),
            ),
          ].filter((name): name is string => !!name);

          if (connectionNames.length > 0) {
            this.fileUploadService
              .getOperationsByConnectionNames(connectionNames, skipLoader)
              .pipe(takeUntil(this.destroy$))
              .subscribe((ops) => {
                ops.forEach((item) => {
                  this.connectionOperationsMap.set(
                    item.connection_name,
                    item.operations,
                  );
                });
              });
          }
        }
        setTimeout(() => this.findFileItem());
      });
  }

  onUploadPopupClose(isClickedOutside: boolean): void {
    if (isClickedOutside) {
      return;
    }
    this.showUploadPopup = false;
  }

  getFileOperations(): void {
    this.fileUploadService
      .getFileOperations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fileOperations = response;

          if (this.isAdmin) {
            // add all operations for admin users
            this.operationOptions = [
              { option: 'All', value: 'all' },
              { option: 'Visualize Data', value: '1' },
              { option: 'Import Data', value: '2' },
              { option: 'Portfolio Mining', value: '3' },
            ];
          } else {
            // Populate operationOptions based on fileOperations
            this.operationOptions = [
              { option: 'All', value: 'all' },
              ...this.fileOperations.map((op) => ({
                option: op.operation_name,
                value: op.operation_id.toString(),
              })),
            ];
          }
        },
        error: (err) => {
          console.error('Failed to get file operations:', err);
        },
      });
  }

  getConnections(): void {
    this.manageConnectionService
      .getConnectionsFromDB()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Auth0Connection[]) => {
          this.connections = response.map((c) => ({
            id: c.connection_id,
            name: c.connection_display_name,
            display_name: c.connection_display_name,
          })) as Connection[];
        },
        error: (err) => {
          console.error('Failed to get connections:', err);
        },
      });
  }

  onConnectionFilterChange(): void {
    this.uploadedUserFilter = '';
    this.getUsers();
  }

  onTabChange(tab: 'my-files' | 'shared-files' | 'pdm-files'): void {
    if (this.activeTab === tab) return;
    if (tab === 'pdm-files' && !this.isAdmin) return;

    const queryParams: Record<string, string | null> = {
      shared: null,
      pdm_only: null,
    };
    if (tab === 'shared-files') {
      queryParams['shared'] = 'true';
    } else if (tab === 'pdm-files') {
      queryParams['pdm_only'] = 'true';
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private resetFiltersState(): void {
    this.operationsFilter = 'all';
    this.uploadedConnectionFilter = '';
    this.uploadedUserFilter = '';
    this.uploadStatusFilter = '';
    this.appliedOperationsFilter = 'all';
    this.appliedUploadedConnectionFilter = '';
    this.appliedUploadedUserFilter = '';
    this.appliedUploadedUserFilter = '';
    this.appliedUploadStatusFilter = '';
    this.pdmStatusFilter = '';
    this.appliedPdmStatusFilter = '';
    this.currentPage = 1;
    this.loading = true;
  }

  get hasActiveFilters(): boolean {
    return (
      this.appliedOperationsFilter !== 'all' ||
      this.appliedUploadStatusFilter !== '' ||
      this.appliedUploadedConnectionFilter !== '' ||
      this.appliedUploadedConnectionFilter !== '' ||
      this.appliedUploadedUserFilter !== '' ||
      this.appliedPdmStatusFilter !== ''
    );
  }

  get activeFilters(): ActiveFilter[] {
    const filters = [];

    if (this.appliedOperationsFilter !== 'all') {
      const op = this.operationOptions.find(
        (o) => o.value === this.appliedOperationsFilter,
      );
      filters.push({
        type: 'operationsFilter',
        label: 'Operation',
        value: this.appliedOperationsFilter,
        displayValue: op ? op.option : this.appliedOperationsFilter,
      });
    }

    if (this.appliedUploadStatusFilter !== '') {
      let statusLabel = '';
      switch (this.appliedUploadStatusFilter) {
        case '1':
          statusLabel = 'Pending';
          break;
        case '2':
          statusLabel = 'Success';
          break;
        case '3':
          statusLabel = 'Failed';
          break;
        case '4':
          statusLabel = 'Malicious';
          break;
      }
      filters.push({
        type: 'uploadStatusFilter',
        label: 'Status',
        value: this.appliedUploadStatusFilter,
        displayValue: statusLabel,
      });
    }

    if (this.appliedUploadedConnectionFilter !== '') {
      const connection = this.connections.find(
        (c) => c.name === this.appliedUploadedConnectionFilter,
      );
      filters.push({
        type: 'uploadedConnectionFilter',
        label: 'CDFI',
        value: this.appliedUploadedConnectionFilter,
        displayValue: connection
          ? connection.display_name || connection.name
          : this.appliedUploadedConnectionFilter,
      });
    }

    if (this.appliedUploadedUserFilter !== '') {
      const user = this.users.find(
        (u) => u.username === this.appliedUploadedUserFilter,
      );
      filters.push({
        type: 'uploadedUserFilter',
        label: 'User',
        value: this.appliedUploadedUserFilter,
        displayValue: user
          ? `${user.first_name} ${user.last_name || ''}`.trim()
          : this.appliedUploadedUserFilter,
      });
    }

    if (this.appliedPdmStatusFilter !== '') {
      let statusLabel = '';
      const statusValue = Number(this.appliedPdmStatusFilter);
      switch (statusValue) {
        case SharingStatus.NOT_UPLOADED:
          statusLabel = 'Not Uploaded';
          break;
        case SharingStatus.PENDING_APPROVAL:
          statusLabel = 'Pending Approval';
          break;
        case SharingStatus.APPROVED:
          statusLabel = 'Approved';
          break;
        case SharingStatus.REJECTED:
          statusLabel = 'Rejected';
          break;
      }
      filters.push({
        type: 'pdmStatusFilter',
        label: 'PDM Status',
        value: this.appliedPdmStatusFilter,
        displayValue: statusLabel,
      });
    }

    return filters;
  }

  removeFilter(filter: ActiveFilter): void {
    switch (filter.type) {
      case 'operationsFilter':
        this.operationsFilter = 'all';
        this.appliedOperationsFilter = 'all';
        break;
      case 'uploadStatusFilter':
        this.uploadStatusFilter = '';
        this.appliedUploadStatusFilter = '';
        break;
      case 'uploadedConnectionFilter':
        this.uploadedConnectionFilter = '';
        this.appliedUploadedConnectionFilter = '';
        this.onConnectionFilterChange(); // This also clears user filter and reloads
        return; // onConnectionFilterChange calls loadFiles
      case 'uploadedUserFilter':
        this.uploadedUserFilter = '';
        this.appliedUploadedUserFilter = '';
        break;
      case 'pdmStatusFilter':
        this.pdmStatusFilter = '';
        this.appliedPdmStatusFilter = '';
        break;
    }
    this.currentPage = 1;
    this.loadFiles();
  }

  getUsers(): void {
    const connections = this.uploadedConnectionFilter
      ? [this.uploadedConnectionFilter]
      : [];
    this.manageUsersService
      .getAllUsers(1, 100, connections, '', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.users = response.results;
        },
        error: (err) => {
          console.error('Failed to get users:', err);
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadFiles();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadFiles();
    }
  }

  onFileUploaded(): void {
    this.loadFiles();
  }

  onUploadPopupShouldClose(): void {
    this.showUploadPopup = false;
  }

  onDownloadFile(file: FileDetails, performActualDownload = false): void {
    // Prevent download if file is still uploading
    if (file.upload_status === FileUploadStatus.Pending) {
      this.showToastMessage(
        'File is still uploading. Please wait for upload to complete.',
        'error',
      );
      return;
    }

    // Prevent download if file is malicious
    if (file.upload_status === FileUploadStatus.Malicious) {
      this.showToastMessage(
        'This file was identified as malicious during security scanning and is blocked from download.',
        'error',
      );
      return;
    }

    if (performActualDownload) {
      this.auditLogService.logFeature({
        featureName: 'file_download',
        action: 'click',
        meta: { fileName: file.blob_name, fileId: String(file.id) },
        description: 'User clicks to download a document',
      });
    } else {
      // Just opening the viewer without downloading immediately
      this.auditLogService.logFeature({
        featureName: 'file_view',
        action: 'click',
        meta: { fileName: file.blob_name, fileId: String(file.id) },
        description: 'User clicks to view a document',
      });
    }

    this.fileUploadService
      .downloadFile(file.id, !performActualDownload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (performActualDownload) {
          const link = document.createElement('a');
          link.href = response.download_url;
          link.download = response.file_name;
          link.click();
        } else {
          this.viewerFileId = file.id;
          this.viewerFileUrl = response.download_url;
          this.viewerFileName = response.file_name;
          this.showFileViewer = true;
        }
      });
  }

  onCloseViewer(): void {
    this.showFileViewer = false;
    this.viewerFileUrl = '';
    this.viewerFileName = '';
    this.viewerFileId = null;
  }

  onViewerDownload(): void {
    if (this.viewerFileId) {
      this.fileUploadService
        .downloadFile(this.viewerFileId, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          const link = document.createElement('a');
          link.href = response.download_url;
          link.download = response.file_name;
          link.click();
        });
    }
  }

  onDeleteFile(file: FileDetails): void {
    this.popupFile = file;
    this.showConfirmPopup = true;
  }

  onConfirmDelete(): void {
    if (!this.popupFile) return;

    this.auditLogService.logFeature({
      featureName: 'file_delete',
      action: 'confirm',
      meta: {
        fileName: this.popupFile.blob_name,
        fileId: String(this.popupFile.id),
      },
      description: 'User confirms document deletion',
    });

    this.fileUploadService
      .deleteFile(this.popupFile.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showConfirmPopup = false;
          this.currentPage =
            this.uploadedFiles.length == 1
              ? this.currentPage - 1
              : this.currentPage;
          if (this.currentPage == 0) this.currentPage = 1;
          this.loadFiles();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  onSharingStatusClick(file: FileDetails): void {
    if (file.sharing_status === SharingStatus.NOT_UPLOADED) return;
    this.approveFile = file;
    this.approveSharingInfo = null;
    this.loadingSharingInfo = true;
    this.showApprovePopup = true;

    this.fileUploadService
      .getPdmFileSharingInfo(file.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          this.approveSharingInfo = info;
          this.loadingSharingInfo = false;
        },
        error: (err) => {
          console.error('Failed to load sharing info:', err);
          this.loadingSharingInfo = false;
        },
      });
  }

  get isSharingApprovalActionable(): boolean {
    return this.approveFile?.sharing_status === SharingStatus.PENDING_APPROVAL;
  }

  get approvePopupHeading(): string {
    return this.isSharingApprovalActionable
      ? 'Review Sharing'
      : 'Sharing Details';
  }

  onConfirmApprove(): void {
    this.processApproval(PDMShareAction.APPROVE);
  }

  onRejectApproval(isClosePopup: boolean): void {
    if (!isClosePopup) {
      this.processApproval(PDMShareAction.REJECT);
    } else this.showApprovePopup = false;
  }

  private processApproval(action: number): void {
    if (!this.approveFile) return;

    const isApprove = action === PDMShareAction.APPROVE;

    this.auditLogService.logFeature({
      featureName: isApprove ? 'approve_pdm_file' : 'reject_pdm_file',
      action: isApprove ? 'approve' : 'reject',
      meta: {
        fileName: this.approveFile.blob_name,
        fileId: String(this.approveFile.id),
      },
      description: isApprove
        ? 'Admin approves a PDM file'
        : 'Admin rejects a PDM file',
    });

    const successMsg = isApprove
      ? 'File sharing approved successfully'
      : 'File sharing rejected successfully';
    const errorMsg = isApprove
      ? 'Failed to approve file sharing'
      : 'Failed to reject file sharing';

    this.fileUploadService
      .submitFileApproval(this.approveFile.id, action)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showApprovePopup = false;
          this.showToastMessage(successMsg, 'success');
          this.loadFiles();
        },
        error: (err) => {
          console.error(err);
          this.showToastMessage(errorMsg, 'error');
        },
      });
  }

  onOperationsFilterChange(value: string): void {
    this.operationsFilter = value;
    this.loadFiles();
  }

  @ViewChild(FilterGroupComponent) filterGroup!: FilterGroupComponent;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.filterGroup && this.filterGroup.isOpen && window.scrollY > 0) {
      this.filterGroup.closeFilter();
    }
  }

  applyFilters(): void {
    if (this.filterGroup) {
      this.filterGroup.closeFilter();
    }
    // Sync applied filters with current filter values
    this.appliedOperationsFilter = this.operationsFilter;
    this.appliedUploadedConnectionFilter = this.uploadedConnectionFilter;
    this.appliedUploadedUserFilter = this.uploadedUserFilter;
    this.appliedUploadStatusFilter = this.uploadStatusFilter;
    this.appliedPdmStatusFilter = this.pdmStatusFilter;
    this.currentPage = 1;
    this.loadFiles();
  }

  clearFilters(): void {
    this.operationsFilter = 'all';
    this.uploadedConnectionFilter = '';
    this.uploadedUserFilter = '';
    this.uploadStatusFilter = '';
    this.appliedOperationsFilter = 'all';
    this.appliedUploadedConnectionFilter = '';
    this.appliedUploadedUserFilter = '';
    this.appliedUploadedUserFilter = '';
    this.appliedUploadStatusFilter = '';
    this.pdmStatusFilter = '';
    this.appliedPdmStatusFilter = '';
    this.currentPage = 1;
    this.loadFiles();
    this.getUsers();
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadFiles();
  }

  onShareFile(file: FileDetails): void {
    // Prevent sharing if file is still uploading
    if (file.upload_status === FileUploadStatus.Pending) {
      this.showToastMessage(
        'Cannot share file while upload is in progress.',
        'error',
      );
      return;
    }

    this.auditLogService.logFeature({
      featureName: 'file_share',
      action: 'share',
      meta: { fileName: file.blob_name, fileId: String(file.id) },
      description: 'User shares a document with others',
    });

    this.shareFile = file;
    this.showSharePopup = true;
  }

  onSharePopupClosed(): void {
    this.showSharePopup = false;
  }

  onShowOperations(file: FileDetails): void {
    this.operationsFile = file;
    this.showOperationsPopup = true;
  }

  onOperationsPopupClose(): void {
    this.showOperationsPopup = false;
  }

  onShareCompleted(event: {
    message: string;
    type: 'success' | 'error';
  }): void {
    this.showToastMessage(event.message, event.type);
  }

  onFilesNeedRefresh(): void {
    this.loadFiles();
  }

  onRetriggerOperationCompleted(): void {
    this.showOperationsPopup = false;
    this.loadFiles(true);
  }

  onShowToast(event: { message: string; type: 'success' | 'error' }): void {
    this.showToastMessage(event.message, event.type);
  }

  showToastMessage(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  isOlderThan24Hours(uploadedDateTime: string | Date): boolean {
    const uploadDate = new Date(uploadedDateTime);
    const now = new Date();
    const hoursDiff = (now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60);
    return hoursDiff >= 24;
  }
}
